import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onboardingApi } from './services/onboardingApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, AlertTriangle, Loader2, Lock, Mail, Building2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import EditableAccountTable from './components/EditableAccountTable';
import { Info } from 'lucide-react';

interface CustomerInvitation {
  id: string;
  project_id: string;
  invitation_token: string;
  invited_email: string;
  invited_name: string;
  access_level: 'view_only' | 'view_edit';
  expires_at: string;
  status: string;
  include_missing_data: boolean;
  include_errors: boolean;
  include_all_accounts: boolean;
  personal_message?: string;
  project?: {
    project_name: string;
    customer_name: string;
  };
}

export default function CustomerPortal() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [invitation, setInvitation] = useState<CustomerInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [properties, setProperties] = useState<Array<{ code: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [contributions, setContributions] = useState<Record<string, any>>({});

  useEffect(() => {
    if (token) {
      loadInvitation();
    }
  }, [token]);

  const loadInvitation = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const data = await onboardingApi.getCustomerInvitation(token);
      
      // Check if invitation is valid
      if (data.status !== 'active') {
        setError('This invitation is no longer active.');
        return;
      }
      
      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired.');
        return;
      }
      
      setInvitation(data);
      
      // Load accounts that need configuration
      await loadAccounts(data);
      
      // Update access tracking
      await updateAccessTracking(data.id);
      
    } catch (err: any) {
      console.error('Failed to load invitation:', err);
      setError(err.message || 'Invalid invitation link. Please contact your project manager.');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async (inv: CustomerInvitation) => {
    try {
      // Load project to get workspace_id for vendors
      const { data: project } = await supabase
        .from('tum_onboarding_projects')
        .select('workspace_id')
        .eq('id', inv.project_id)
        .single();
      
      // Load vendors for the workspace
      if (project?.workspace_id) {
        const { data: vendorsData } = await supabase
          .from('tum_utility_vendors')
          .select('id, vendor_name')
          .eq('workspace_id', project.workspace_id)
          .order('vendor_name', { ascending: true });
        
        setVendors(vendorsData?.map(v => ({ id: v.id, name: v.vendor_name })) || []);
      }
      
      // Load account configurations
      const { data: configs, error: configError } = await (supabase as any)
        .from('tum_onboarding_account_configurations')
        .select('*')
        .eq('project_id', inv.project_id);
      
      if (configError) {
        console.error('Failed to load configurations:', configError);
      }
      
      // If no configurations exist, try loading from staged data
      if (!configs || configs.length === 0) {
        // Load staged data to create initial configurations
        const { data: batches } = await supabase
          .from('tum_onboarding_import_batches')
          .select('id')
          .eq('project_id', inv.project_id)
          .in('batch_type', ['properties', 'accounts'])
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (batches && batches.length > 0) {
          const { data: stagedData } = await supabase
            .from('tum_onboarding_staged_data')
            .select('id, row_number, mapped_data')
            .eq('batch_id', batches[0].id)
            .not('mapped_data->account_number', 'is', null);
          
          if (stagedData && stagedData.length > 0) {
            // Create account rows from staged data
            const accountRows = stagedData.map((row: any) => {
              const data = row.mapped_data || {};
              return {
                id: row.id,
                row_number: row.row_number || 0,
                account_number: data.account_number || '',
                property_code: data.property_name || data.property_code || '',
                property_name: data.property_name || '',
                unit_number: data.unit_number || '',
                commodity_type: data.utility_type || data.commodity_type || '',
                vendor_name: data.supplier_name || data.vendor_name || '[Required]',
                vendor_id: undefined,
                status: (data.supplier_name || data.vendor_name) ? 'complete' as const : 'missing_data' as const,
                errors: [],
                warnings: [],
              };
            });
            
            setAccounts(accountRows);
            return;
          }
        }
      }
      
      // Filter accounts based on invitation settings
      let filteredConfigs = configs || [];
      
      if (inv.include_missing_data && !inv.include_all_accounts) {
        filteredConfigs = filteredConfigs.filter((c: any) => !c.vendor_name || c.vendor_name === '[Required]');
      }
      
      if (inv.include_errors && !inv.include_all_accounts) {
        filteredConfigs = filteredConfigs.filter((c: any) => c.configuration_status === 'incomplete' || c.configuration_status === 'error');
      }
      
      // Transform to account rows format
      const accountRows = filteredConfigs.map((config: any) => ({
        id: config.staging_account_id || config.id,
        row_number: 0,
        account_number: config.account_number || '',
        property_code: '',
        property_name: '',
        unit_number: '',
        commodity_type: '',
        vendor_name: config.vendor_name || '[Required]',
        vendor_id: config.vendor_id,
        status: config.vendor_name && config.vendor_name !== '[Required]' ? 'complete' as const : 'missing_data' as const,
        errors: Array.isArray(config.blocking_issues) ? config.blocking_issues : [],
        warnings: Array.isArray(config.warnings) ? config.warnings : [],
      }));
      
      setAccounts(accountRows);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const updateAccessTracking = async (invitationId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Update invitation access tracking
      const { data: existing } = await (supabase as any)
        .from('tum_onboarding_customer_invitations')
        .select('access_count, first_accessed_at')
        .eq('id', invitationId)
        .single();
      
      await (supabase as any)
        .from('tum_onboarding_customer_invitations')
        .update({
          last_accessed_at: new Date().toISOString(),
          access_count: ((existing as any)?.access_count || 0) + 1,
          first_accessed_at: (existing as any)?.first_accessed_at || new Date().toISOString(),
        })
        .eq('id', invitationId);
    } catch (err) {
      console.error('Failed to update access tracking:', err);
    }
  };

  const handleCellEdit = (rowId: string, field: string, value: any) => {
    setAccounts(prev => prev.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
    
    // Track contribution
    setContributions(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [field]: { old: prev[rowId]?.[field]?.new || accounts.find(a => a.id === rowId)?.[field], new: value }
      }
    }));
  };

  const handleSaveContributions = async () => {
    if (!invitation || !token) return;
    
    try {
      setSubmitting(true);
      
      // Save each contribution
      for (const [accountId, changes] of Object.entries(contributions)) {
        for (const [field, values] of Object.entries(changes as Record<string, { old?: unknown; new?: unknown }>)) {
          await (supabase as any)
            .from('tum_onboarding_customer_contributions')
            .insert({
              invitation_id: invitation.id,
              project_id: invitation.project_id,
              account_configuration_id: accountId,
              field_name: field,
              old_value: (values as any).old?.toString() || '',
              new_value: (values as any).new?.toString() || '',
              contributed_by_email: invitation.invited_email,
            });
        }
      }
      
      // Update account configurations
      for (const account of accounts) {
        if (contributions[account.id]) {
          await onboardingApi.saveAccountConfiguration(
            invitation.project_id,
            account.id,
            {
              account_number: account.account_number,
              vendor_name: account.vendor_name,
              vendor_id: account.vendor_id,
            }
          );
        }
      }
      
      toast({
        title: 'Contributions saved',
        description: 'Your changes have been saved successfully.',
      });
      
      // Reload to show updated status
      await loadAccounts(invitation);
      setContributions({});
      
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to save contributions',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Invalid Invitation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                {error || 'This invitation link is invalid or has expired. Please contact your project manager for a new invitation.'}
              </AlertDescription>
            </Alert>
            <Button 
              className="mt-4 w-full" 
              onClick={() => window.location.href = '/'}
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasChanges = Object.keys(contributions).length > 0;
  const canEdit = invitation.access_level === 'view_edit';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {invitation.project?.project_name || 'Onboarding Project'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Customer: {invitation.project?.customer_name || invitation.invited_name}
              </p>
            </div>
            <Badge variant={invitation.status === 'active' ? 'default' : 'secondary'}>
              {invitation.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Welcome, {invitation.invited_name || invitation.invited_email}!
            </CardTitle>
            <CardDescription>
              {invitation.personal_message || 
                'We need your help to complete the account configuration. Please review and update the information below.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>Project: <strong>{invitation.project?.project_name}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <span>Access Level: <strong>{invitation.access_level === 'view_edit' ? 'Edit' : 'View Only'}</strong></span>
              </div>
              {invitation.expires_at && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span>Expires: <strong>{new Date(invitation.expires_at).toLocaleDateString()}</strong></span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>What we need from you:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {invitation.include_missing_data && (
                <li>Complete missing vendor information for your accounts</li>
              )}
              {invitation.include_errors && (
                <li>Fix any errors in the account data</li>
              )}
              <li>Verify that all information is accurate</li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Account Configuration Table */}
        {accounts.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Account Configuration</h2>
              {hasChanges && canEdit && (
                <Button onClick={handleSaveContributions} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              )}
            </div>

            {canEdit ? (
              <EditableAccountTable
                accounts={accounts}
                properties={properties}
                vendors={vendors}
                commodities={[]}
                onCellEdit={handleCellEdit}
                onBulkEdit={() => {}}
                onRowClick={() => {}}
                onExport={() => {}}
                showConfiguration={false}
              />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground text-center py-8">
                    You have view-only access. Please contact your project manager to request edit access.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            {hasChanges && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-sm">Pending Changes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">
                    You have {Object.keys(contributions).length} account{Object.keys(contributions).length !== 1 ? 's' : ''} with unsaved changes.
                    Click "Save Changes" above to submit your updates.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">All Set!</h3>
                <p className="text-muted-foreground">
                  All accounts have been configured. No action needed from you at this time.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

